<!-- eslint-disable tailwindcss/no-custom-classname -->
<template>
  <!-- Mobile Sidebar -->
  <div class="mobile-menu-container fixed inset-x-0 top-0 z-[9999] border-b bg-menu md:hidden">
    <div
      class="flex items-center justify-between px-4 py-3"
      :class="isBulletInAvailable ? 'mt-11' : ''"
    >
      <RouterLink to="/" class="flex items-center">
        <img alt="Co-Assign" src="@/assets/co-assign.png" width="20" height="20" />
        <span class="ml-2 text-lg font-semibold">Co-Assign</span>
      </RouterLink>
      <button
        class="rounded-md p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
        :class="{ 'bg-gray-100 dark:bg-gray-700': isMobileMenuOpen }"
        @click="toggleMobileMenu"
      >
        <svg
          class="size-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            v-if="!isMobileMenuOpen"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 6h16M4 12h16M4 18h16"
          />
          <path
            v-else
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
    <div
      v-if="isMobileMenuOpen"
      class="absolute inset-x-0 top-full z-[9998] max-h-[calc(100vh-80px)] overflow-y-auto border-b bg-menu shadow-lg"
    >
      <div class="space-y-4 p-4">
        <!-- Mobile Menu Items -->
        <div v-if="canReadResource" class="space-y-2">
          <button
            class="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
            @click="toggleResourceMenu"
          >
            <div class="flex items-center">
              <span
                class="material-symbols-outlined notranslate mr-3 text-lg"
                translate="no"
                aria-hidden="true"
                >format_list_bulleted</span
              >
              {{ $t('ui.sideMenu.resource') }}
            </div>
            <span
              class="material-symbols-outlined notranslate text-sm transition-transform"
              :class="{ 'rotate-180': isResourceMenuOpen }"
              translate="no"
              aria-hidden="true"
            >
              expand_more
            </span>
          </button>
          <div v-if="isResourceMenuOpen" class="ml-6 space-y-1">
            <RouterLink
              v-if="canReadUserProject"
              :to="`/userproject/${format(new Date(), 'yyyy-MM-dd')}`"
              class="block rounded-md px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
              @click="closeMobileMenu"
            >
              {{ $t('ui.sideMenu.userProject') }}
            </RouterLink>
            <RouterLink
              v-if="canReadProjectUser"
              :to="`/projectuser/${format(new Date(), 'yyyy-MM-dd')}`"
              class="block rounded-md px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
              @click="closeMobileMenu"
            >
              {{ $t('ui.sideMenu.projectUser') }}
            </RouterLink>
          </div>
        </div>

        <RouterLink
          v-if="canReadUser"
          to="/user"
          class="flex items-center rounded-md px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
          @click="closeMobileMenu"
        >
          <span
            class="material-symbols-outlined notranslate mr-3 text-lg"
            translate="no"
            aria-hidden="true"
            >group</span
          >
          {{ $t('stages.table.user') }}
        </RouterLink>

        <RouterLink
          v-if="canReadProject"
          to="/project"
          class="flex items-center rounded-md px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
          @click="closeMobileMenu"
        >
          <span
            class="material-symbols-outlined notranslate mr-3 text-lg"
            translate="no"
            aria-hidden="true"
            >draft</span
          >
          {{ $t('stages.table.project') }}
        </RouterLink>

        <RouterLink
          v-if="canReadAttendance"
          :to="`/worksheet/${format(new Date(), 'yyyy-MM')}`"
          class="flex items-center rounded-md px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
          @click="closeMobileMenu"
        >
          <span
            class="material-symbols-outlined notranslate mr-3 text-lg"
            translate="no"
            aria-hidden="true"
            >edit_calendar</span
          >
          {{ $t('ui.sideMenu.attendance') }}
        </RouterLink>

        <RouterLink
          v-if="canReadJob"
          to="/job"
          class="flex items-center rounded-md px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
          @click="closeMobileMenu"
        >
          <span
            class="material-symbols-outlined notranslate mr-3 text-lg"
            translate="no"
            aria-hidden="true"
            >equalizer</span
          >
          {{ $t('ui.sideMenu.dataReport') }}
        </RouterLink>

        <div v-if="canReadSettingMenu" class="space-y-2">
          <button
            class="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
            @click="toggleSettingMenu"
          >
            <div class="flex items-center">
              <span
                class="material-symbols-outlined notranslate mr-3 text-lg"
                translate="no"
                aria-hidden="true"
                >settings</span
              >
              {{ $t('ui.sideMenu.settings') }}
            </div>
            <span
              class="material-symbols-outlined notranslate text-sm transition-transform"
              :class="{ 'rotate-180': isSettingMenuOpen }"
              translate="no"
              aria-hidden="true"
            >
              expand_more
            </span>
          </button>
          <div v-if="isSettingMenuOpen" class="ml-6 space-y-1">
            <RouterLink
              v-if="canReadDepartment"
              to="/department"
              class="flex items-center rounded-md px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
              @click="closeMobileMenu"
            >
              <span
                class="material-symbols-outlined notranslate mr-3 text-lg"
                translate="no"
                aria-hidden="true"
                >business</span
              >
              {{ $t('stages.table.department') }}
            </RouterLink>
            <RouterLink
              v-if="canReadClient"
              to="/client"
              class="flex items-center rounded-md px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
              @click="closeMobileMenu"
            >
              <span
                class="material-symbols-outlined notranslate mr-3 text-lg"
                translate="no"
                aria-hidden="true"
                >business_center</span
              >
              {{ $t('stages.table.client') }}
            </RouterLink>
            <RouterLink
              v-if="canReadPartner"
              to="/partner"
              class="flex items-center rounded-md px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
              @click="closeMobileMenu"
            >
              <span
                class="material-symbols-outlined notranslate mr-3 text-lg"
                translate="no"
                aria-hidden="true"
                >group_work</span
              >
              {{ $t('ui.sideMenu.partner') }}
            </RouterLink>
            <RouterLink
              v-if="canReadJobTitle"
              to="/job_title"
              class="flex items-center rounded-md px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
              @click="closeMobileMenu"
            >
              <span
                class="material-symbols-outlined notranslate mr-3 text-lg"
                translate="no"
                aria-hidden="true"
                >work</span
              >
              {{ $t('stages.table.job_title') }}
            </RouterLink>
            <RouterLink
              v-if="canReadJobSkill"
              to="/job_skill"
              class="flex items-center rounded-md px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
              @click="closeMobileMenu"
            >
              <span
                class="material-symbols-outlined notranslate mr-3 text-lg"
                translate="no"
                aria-hidden="true"
                >psychology</span
              >
              {{ $t('stages.table.job_skill') }}
            </RouterLink>
            <RouterLink
              v-if="canReadCustomField"
              to="/custom_field"
              class="flex items-center rounded-md px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
              @click="closeMobileMenu"
            >
              <span
                class="material-symbols-outlined notranslate mr-3 text-lg"
                translate="no"
                aria-hidden="true"
                >tune</span
              >
              {{ $t('stages.table.custom_field') }}
            </RouterLink>
            <RouterLink
              v-if="canReadHoliday"
              to="/holiday"
              class="flex items-center rounded-md px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
              @click="closeMobileMenu"
            >
              <span
                class="material-symbols-outlined notranslate mr-3 text-lg"
                translate="no"
                aria-hidden="true"
                >event</span
              >
              {{ $t('stages.table.holiday') }}
            </RouterLink>
            <RouterLink
              v-if="canReadSetting"
              to="/setting"
              class="flex items-center rounded-md px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
              @click="closeMobileMenu"
            >
              <span
                class="material-symbols-outlined notranslate mr-3 text-lg"
                translate="no"
                aria-hidden="true"
                >settings</span
              >
              {{ $t('stages.table.setting') }}
            </RouterLink>
          </div>
        </div>

        <div class="border-t pt-4">
          <div class="flex items-center px-3 py-2">
            <UserIcon :user-name="userStore.userInfo?.userName || ''" />
            <span class="ml-2 text-sm">{{ userStore.userInfo?.userName || '' }}</span>
          </div>
          <RouterLink
            v-if="canChangeMyPassword"
            to="/change_password"
            class="flex items-center rounded-md px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
            @click="closeMobileMenu"
          >
            <span
              class="material-symbols-outlined notranslate mr-3 text-lg"
              translate="no"
              aria-hidden="true"
              >lock</span
            >
            {{ $t('ui.sideMenu.changePassword') }}
          </RouterLink>
          <RouterLink
            to="/logout"
            class="flex items-center rounded-md px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
            @click="closeMobileMenu"
          >
            <span
              class="material-symbols-outlined notranslate mr-3 text-lg"
              translate="no"
              aria-hidden="true"
              >logout</span
            >
            {{ $t('ui.sideMenu.logout') }}
          </RouterLink>
        </div>
      </div>
    </div>
  </div>

  <!-- PC Sidebar -->
  <div
    class="hidden shrink-0 flex-col border-r bg-menu pt-3 md:flex"
    :class="[isWide ? 'w-[175px]' : 'w-[50px]', isBulletInAvailable ? 'mt-11' : '']"
  >
    <div class="space-y-6 pl-3">
      <div class="flex pt-1">
        <RouterLink to="/">
          <div class="flex">
            <img alt="Co-Assign" src="@/assets/co-assign.png" width="20" height="20" />
            <span v-if="isWide" class="my-auto ml-1">Co-Assign</span>
          </div>
        </RouterLink>
      </div>

      <div class="flex w-full flex-col space-y-3">
        <div v-if="canReadResource" class="hs-dropdown flex items-center [--placement:right-top]">
          <SideMenuDropdownIconButton
            :is-visible-tool-tip="!isWide"
            dropdown-id="dropdown-resource"
          >
            <template #icon>format_list_bulleted</template>
            <template v-if="isWide" #label
              ><span class="my-auto ml-1">{{ $t('ui.sideMenu.resource') }}</span></template
            >
            <template #popper>{{ $t('ui.sideMenu.resource') }}</template>
          </SideMenuDropdownIconButton>
          <div
            class="hs-dropdown-menu duration z-[110] hidden w-48 rounded-lg border bg-menu opacity-0 shadow-lg transition-[opacity,margin] hs-dropdown-open:opacity-100"
            aria-labelledby="dropdown-resource"
          >
            <ul>
              <RouterLink
                v-if="canReadUserProject"
                :to="`/userproject/${format(new Date(), 'yyyy-MM-dd')}`"
              >
                <li class="submenu">{{ $t('ui.sideMenu.userProject') }}</li>
              </RouterLink>
              <RouterLink
                v-if="canReadProjectUser"
                :to="`/projectuser/${format(new Date(), 'yyyy-MM-dd')}`"
              >
                <li class="submenu">{{ $t('ui.sideMenu.projectUser') }}</li>
              </RouterLink>
            </ul>
          </div>
        </div>
        <SideMenuIconButton v-if="canReadUser" :is-visible-tool-tip="!isWide" to="/user">
          <template #icon>group</template>
          <template v-if="isWide" #label
            ><span class="my-auto ml-1">{{ $t('stages.table.user') }}</span></template
          >
          <template #popper>{{ $t('stages.table.user') }}</template>
        </SideMenuIconButton>
        <SideMenuIconButton v-if="canReadProject" :is-visible-tool-tip="!isWide" to="/project">
          <template #icon>draft</template>
          <template v-if="isWide" #label
            ><span class="my-auto ml-1">{{ $t('stages.table.project') }}</span></template
          >
          <template #popper>{{ $t('stages.table.project') }}</template>
        </SideMenuIconButton>
        <a v-if="canReadAttendance" class="flex">
          <SideMenuIconButton
            :is-visible-tool-tip="!isWide"
            :to="`/worksheet/${format(new Date(), 'yyyy-MM')}`"
          >
            <template #icon>edit_calendar</template>
            <template v-if="isWide" #label
              ><span class="my-auto ml-1">{{ $t('ui.sideMenu.attendance') }}</span></template
            >
            <template #popper>{{ $t('ui.sideMenu.attendance') }}</template>
          </SideMenuIconButton>
        </a>

        <SideMenuIconButton v-if="canReadJob" :is-visible-tool-tip="!isWide" to="/job">
          <template #icon>equalizer</template>
          <template v-if="isWide" #label
            ><span class="my-auto ml-1">{{ $t('ui.sideMenu.dataReport') }}</span></template
          >
          <template #popper>{{ $t('ui.sideMenu.dataReport') }}</template>
        </SideMenuIconButton>

        <div
          v-if="canReadSettingMenu"
          class="hs-dropdown flex items-center [--placement:right-top]"
        >
          <SideMenuDropdownIconButton
            :is-visible-tool-tip="!isWide"
            dropdown-id="dropdown-settings"
          >
            <template #icon>settings</template>
            <template v-if="isWide" #label>
              <span class="my-auto ml-1">{{ $t('ui.sideMenu.settings') }}</span>
            </template>
            <template #popper>{{ $t('ui.sideMenu.settings') }}</template>
          </SideMenuDropdownIconButton>
          <div
            class="hs-dropdown-menu duration z-[110] hidden w-48 rounded-lg border bg-menu opacity-0 shadow-md transition-[opacity,margin] hs-dropdown-open:opacity-100"
            aria-labelledby="dropdown-settings"
          >
            <ul class="bg-menu dark:text-gray-200">
              <RouterLink v-if="canReadDepartment" to="/department">
                <li class="submenu">{{ $t('stages.table.department') }}</li>
              </RouterLink>
              <RouterLink v-if="canReadClient" to="/client">
                <li class="submenu">{{ $t('stages.table.client') }}</li>
              </RouterLink>
              <RouterLink v-if="canReadPartner" to="/partner">
                <li class="submenu">{{ $t('ui.sideMenu.partner') }}</li>
              </RouterLink>
              <RouterLink v-if="canReadJobTitle" to="/job_title">
                <li class="submenu">{{ $t('stages.table.job_title') }}</li>
              </RouterLink>
              <RouterLink v-if="canReadJobSkill" to="/job_skill">
                <li class="submenu">{{ $t('stages.table.job_skill') }}</li>
              </RouterLink>
              <RouterLink v-if="canReadCustomField" to="/custom_field">
                <li class="submenu">{{ $t('stages.table.custom_field') }}</li>
              </RouterLink>
              <RouterLink v-if="canReadHoliday" to="/holiday">
                <li class="submenu">{{ $t('stages.table.holiday') }}</li>
              </RouterLink>
              <RouterLink v-if="canReadSetting" to="/setting">
                <li class="submenu">{{ $t('stages.table.setting') }}</li>
              </RouterLink>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div class="flex-1"></div>
    <div class="mb-4 flex w-full flex-col pl-3">
      <div class="hs-dropdown [--placement:right-bottom]">
        <button id="dropdown-account" type="button" class="flex">
          <UserIcon :user-name="userStore.userInfo?.userName || ''" />
          <span v-if="isWide" class="my-auto ml-1">{{ userStore.userInfo?.userName || '' }}</span>
        </button>
        <div
          class="hs-dropdown-menu duration z-[110] hidden w-48 rounded-lg border bg-menu opacity-0 shadow-md transition-[opacity,margin] hs-dropdown-open:opacity-100"
          aria-labelledby="dropdown-account"
        >
          <ul class="dark:text-gray-200">
            <li class="flex border-b p-4">
              <UserIcon :user-name="userStore.userInfo?.userName || ''" />
              <span class="my-auto ml-1">{{ userStore.userInfo?.userName || '' }}</span>
            </li>
            <li
              class="flex px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
            >
              <router-link v-if="canChangeMyPassword" to="/change_password">{{
                $t('ui.sideMenu.changePassword')
              }}</router-link>
            </li>
            <li
              class="flex px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
            >
              <router-link to="/logout">{{ $t('ui.sideMenu.logout') }}</router-link>
            </li>
          </ul>
        </div>
      </div>
      <hr class="my-2 mr-3 h-px border-0 bg-gray-200 dark:bg-gray-700" />
      <SimpleIconButton
        button-classes="pl-1 text-left"
        :placement="isWide ? 'top' : 'bottom'"
        @click="clickExpand"
      >
        <template v-if="isWide" #icon>keyboard_double_arrow_left</template>
        <template v-else #icon>keyboard_double_arrow_right</template>
        <template v-if="isWide" #popper>{{ $t('ui.sideMenu.collapse') }}</template>
        <template v-else #popper>{{ $t('ui.sideMenu.expand') }}</template>
      </SimpleIconButton>
      <div class="small-chat-spacer mt-2"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { format } from 'date-fns'
import { useUserStore } from '@/stores/user'
import { canRoute } from '@/lib/canRoute'
import UserIcon from './UserIcon.vue'
import { storeToRefs } from 'pinia'
import { useBulletInStore } from '@/stores/bulletIn'
import SimpleIconButton from '@/components/SimpleIconButton.vue'
import SideMenuIconButton from '@/components/SideMenuIconButton.vue'
import SideMenuDropdownIconButton from '@/components/SideMenuDropdownIconButton.vue'
import { useWindowSize } from '@vueuse/core'

const emits = defineEmits<{
  (e: 'update:isWide', value: boolean): void
}>()

const userStore = useUserStore()

const [
  canReadUserProject,
  canReadProjectUser,
  canReadUser,
  canReadProject,
  canReadAttendance,
  canReadJob,
  canReadDepartment,
  canReadClient,
  canReadPartner,
  canReadJobTitle,
  canReadJobSkill,
  canReadCustomField,
  canReadHoliday,
  canReadSetting,
  canChangeMyPassword
] = await Promise.all([
  canRoute(`/userproject/${format(new Date(), 'yyyy-MM-dd')}`),
  canRoute(`/projectuser/${format(new Date(), 'yyyy-MM-dd')}`),
  canRoute('/user'),
  canRoute('/project'),
  canRoute(`/worksheet/${format(new Date(), 'yyyy-MM')}`),
  canRoute('/job'),
  canRoute('/department'),
  canRoute('/client'),
  canRoute('/partner'),
  canRoute('/job_title'),
  canRoute('/job_skill'),
  canRoute('/custom_field'),
  canRoute('/holiday'),
  canRoute('/setting'),
  canRoute('/change_password')
])

// Top-level menu permissions based on sub-items
const canReadResource = computed(() => canReadUserProject || canReadProjectUser)
const canReadSettingMenu = computed(
  () =>
    canReadDepartment ||
    canReadClient ||
    canReadPartner ||
    canReadJobTitle ||
    canReadJobSkill ||
    canReadCustomField ||
    canReadHoliday ||
    canReadSetting
)

const isWide = ref(true)
const isMobileMenuOpen = ref(false)
const isResourceMenuOpen = ref(false)
const isSettingMenuOpen = ref(false)

const clickExpand = () => {
  isWide.value = !isWide.value
  emits('update:isWide', isWide.value)
}

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
  isResourceMenuOpen.value = false
  isSettingMenuOpen.value = false
}

const toggleResourceMenu = () => {
  isResourceMenuOpen.value = !isResourceMenuOpen.value
}

const toggleSettingMenu = () => {
  isSettingMenuOpen.value = !isSettingMenuOpen.value
}

const { isBulletInAvailable } = storeToRefs(useBulletInStore())
const { width } = useWindowSize()

watch([isWide, width], () => {
  hideMobileChat()
})

const handleClickOutside = (event: Event) => {
  const target = event.target as HTMLElement | null
  if (!target) return
  if (isMobileMenuOpen.value && !target.closest('.mobile-menu-container')) {
    closeMobileMenu()
  }
}

const hideMobileChat = () => {
  const chat = document.getElementById('Smallchat')
  if (chat) {
    chat.style.display = width.value >= 768 && isWide.value ? 'block' : 'none'
  }
}

onMounted(() => {
  nextTick(() => {
    hideMobileChat()
  })

  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
<style scoped>
@tailwind components;
@layer components {
  .submenu {
    @apply flex px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white;
  }
}

@media (max-width: 768px) {
  :global(#Smallchat) {
    display: none !important;
  }
}

.small-chat-spacer {
  height: 30px;
}
</style>
